/**
 * ProjectForms.tsx - Form widgets for project entities
 * Each form opens in a side panel at 50% width
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  X,
  Save,
  Trash2,
  User,
  Users,
  Mail,
  Phone,
  Building,
  Shield,
  FileText,
  Link as LinkIcon,
  ExternalLink,
  AlertTriangle,
  Flag,
  Calendar,
  Clock,
  Tag,
  MessageSquare,
  Paperclip,
  Image,
  Send,
  Star,
  Eye,
  Upload,
  FolderOpen,
  ChevronDown,
  Plus,
} from 'lucide-react';

// =============================================================================
// SHARED STYLES
// =============================================================================

export const formStyles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  panel: {
    width: '50%',
    maxWidth: '600px',
    minWidth: '400px',
    height: '100%',
    background: 'white',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column' as const,
    animation: 'slideIn 0.2s ease-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb',
  },
  headerTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '1.5rem',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e7eb',
    background: '#f9fafb',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '0.9375rem',
    background: 'white',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '0.9375rem',
    background: 'white',
    outline: 'none',
    resize: 'vertical' as const,
    minHeight: '100px',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '0.9375rem',
    background: 'white',
    outline: 'none',
    cursor: 'pointer',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.625rem 1rem',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  buttonPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.625rem 1rem',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    background: '#3b82f6',
    color: 'white',
  },
  buttonSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.625rem 1rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    background: 'white',
    color: '#111827',
    border: '1px solid #e5e7eb',
  },
  buttonDanger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.625rem 1rem',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    background: '#fee2e2',
    color: '#dc2626',
  },
  buttonIcon: {
    background: 'transparent',
    border: 'none',
    padding: '0.5rem',
    cursor: 'pointer',
    color: '#6b7280',
    borderRadius: '0.375rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    display: 'flex',
    gap: '1rem',
  },
  col: {
    flex: 1,
  },
};

// =============================================================================
// 1. MEMBER WIDGET - Team member management
// =============================================================================

export interface MemberFormData {
  name: string;
  email: string;
  phone?: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  department?: string;
  jobTitle?: string;
}

interface MemberWidgetProps {
  isOpen: boolean;
  memberId?: string;
  initialData?: Partial<MemberFormData>;
  onClose: () => void;
  onSave: (data: MemberFormData) => void;
  onDelete?: (id: string) => void;
}

export function MemberWidget({ isOpen, memberId, initialData, onClose, onSave, onDelete }: MemberWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<MemberFormData>({
    name: '',
    email: '',
    phone: '',
    role: 'member',
    department: '',
    jobTitle: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const isEditing = !!memberId;

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <User size={20} />
            {isEditing ? t('projects.widgets.member.editTitle') : t('projects.widgets.member.addTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.member.fullName')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('projects.widgets.member.namePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.member.email')} *</label>
              <input
                type="email"
                style={formStyles.input}
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('projects.widgets.member.emailPlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.member.phone')}</label>
              <input
                type="tel"
                style={formStyles.input}
                value={formData.phone || ''}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('projects.widgets.member.phonePlaceholder')}
              />
            </div>

            <div style={formStyles.row}>
              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.member.role')} *</label>
                <select
                  style={formStyles.select}
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value as MemberFormData['role'] })}
                >
                  <option value="owner">{t('projects.widgets.member.roles.owner')}</option>
                  <option value="admin">{t('projects.widgets.member.roles.admin')}</option>
                  <option value="member">{t('projects.widgets.member.roles.member')}</option>
                  <option value="viewer">{t('projects.widgets.member.roles.viewer')}</option>
                </select>
              </div>

              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.member.department')}</label>
                <input
                  type="text"
                  style={formStyles.input}
                  value={formData.department || ''}
                  onChange={e => setFormData({ ...formData, department: e.target.value })}
                  placeholder={t('projects.widgets.member.departmentPlaceholder')}
                />
              </div>
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.member.jobTitle')}</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.jobTitle || ''}
                onChange={e => setFormData({ ...formData, jobTitle: e.target.value })}
                placeholder={t('projects.widgets.member.jobTitlePlaceholder')}
              />
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(memberId)}
                >
                  <Trash2 size={16} /> {t('projects.widgets.member.remove')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('common.add')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// 2. NOTEBOOK WIDGET - Note blocks management
// =============================================================================

export interface NotebookFormData {
  title: string;
  content: string;
  category?: string;
}

interface NotebookWidgetProps {
  isOpen: boolean;
  notebookId?: string;
  initialData?: Partial<NotebookFormData>;
  onClose: () => void;
  onSave: (data: NotebookFormData) => void;
  onDelete?: (id: string) => void;
}

export function NotebookWidget({ isOpen, notebookId, initialData, onClose, onSave, onDelete }: NotebookWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<NotebookFormData>({
    title: '',
    content: '',
    category: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const isEditing = !!notebookId;

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <FileText size={20} />
            {isEditing ? t('projects.widgets.notebook.editTitle') : t('projects.widgets.notebook.newTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.notebook.title')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('projects.widgets.notebook.titlePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.category')}</label>
              <select
                style={formStyles.select}
                value={formData.category || ''}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="">{t('projects.widgets.notebook.selectCategory')}</option>
                <option value="Tecnico">{t('projects.widgets.notebook.categories.technical')}</option>
                <option value="Documentazione">{t('projects.widgets.notebook.categories.documentation')}</option>
                <option value="Meeting">{t('projects.widgets.notebook.categories.meeting')}</option>
                <option value="Processo">{t('projects.widgets.notebook.categories.process')}</option>
                <option value="Altro">{t('projects.widgets.notebook.categories.other')}</option>
              </select>
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.notebook.content')} *</label>
              <textarea
                style={{ ...formStyles.textarea, minHeight: '300px' }}
                value={formData.content}
                onChange={e => setFormData({ ...formData, content: e.target.value })}
                placeholder={t('projects.widgets.notebook.contentPlaceholder')}
                required
              />
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(notebookId)}
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// 3. LINK WIDGET - Links management
// =============================================================================

export interface LinkFormData {
  title: string;
  url: string;
  description?: string;
  category?: string;
}

interface LinkWidgetProps {
  isOpen: boolean;
  linkId?: string;
  initialData?: Partial<LinkFormData>;
  onClose: () => void;
  onSave: (data: LinkFormData) => void;
  onDelete?: (id: string) => void;
}

export function LinkWidget({ isOpen, linkId, initialData, onClose, onSave, onDelete }: LinkWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LinkFormData>({
    title: '',
    url: '',
    description: '',
    category: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const isEditing = !!linkId;

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <LinkIcon size={20} />
            {isEditing ? t('projects.widgets.link.editTitle') : t('projects.widgets.link.newTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.link.title')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('projects.widgets.link.titlePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.link.url')} *</label>
              <input
                type="url"
                style={formStyles.input}
                value={formData.url}
                onChange={e => setFormData({ ...formData, url: e.target.value })}
                placeholder={t('projects.widgets.link.urlPlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.description')}</label>
              <textarea
                style={formStyles.textarea}
                value={formData.description || ''}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('projects.widgets.link.descriptionPlaceholder')}
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.category')}</label>
              <select
                style={formStyles.select}
                value={formData.category || ''}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="">{t('projects.widgets.link.selectCategory')}</option>
                <option value="Development">{t('projects.widgets.link.categories.development')}</option>
                <option value="Design">{t('projects.widgets.link.categories.design')}</option>
                <option value="Management">{t('projects.widgets.link.categories.management')}</option>
                <option value="Documentation">{t('projects.widgets.link.categories.documentation')}</option>
                <option value="Tools">{t('projects.widgets.link.categories.tools')}</option>
                <option value="Altro">{t('projects.widgets.link.categories.other')}</option>
              </select>
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(linkId)}
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('common.add')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// 4. RISK WIDGET - Risk management
// =============================================================================

export interface RiskFormData {
  title: string;
  description: string;
  probability: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  status: 'identified' | 'mitigating' | 'resolved' | 'accepted';
  owner: string;
  mitigation?: string;
}

interface RiskWidgetProps {
  isOpen: boolean;
  riskId?: string;
  initialData?: Partial<RiskFormData>;
  onClose: () => void;
  onSave: (data: RiskFormData) => void;
  onDelete?: (id: string) => void;
}

export function RiskWidget({ isOpen, riskId, initialData, onClose, onSave, onDelete }: RiskWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<RiskFormData>({
    title: '',
    description: '',
    probability: 'medium',
    impact: 'medium',
    status: 'identified',
    owner: '',
    mitigation: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const isEditing = !!riskId;

  const getRiskLevelLabel = () => {
    if (formData.probability === 'high' && formData.impact === 'high') {
      return t('projects.widgets.risk.riskLevel.high');
    } else if (formData.probability === 'high' || formData.impact === 'high') {
      return t('projects.widgets.risk.riskLevel.medium');
    }
    return t('projects.widgets.risk.riskLevel.low');
  };

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <AlertTriangle size={20} />
            {isEditing ? t('projects.widgets.risk.editTitle') : t('projects.widgets.risk.newTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.risk.riskTitle')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('projects.widgets.risk.titlePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.description')} *</label>
              <textarea
                style={formStyles.textarea}
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('projects.widgets.risk.descriptionPlaceholder')}
                required
              />
            </div>

            <div style={formStyles.row}>
              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.risk.probability')} *</label>
                <select
                  style={formStyles.select}
                  value={formData.probability}
                  onChange={e => setFormData({ ...formData, probability: e.target.value as RiskFormData['probability'] })}
                >
                  <option value="low">{t('projects.risks.probability.low')}</option>
                  <option value="medium">{t('projects.risks.probability.medium')}</option>
                  <option value="high">{t('projects.risks.probability.high')}</option>
                </select>
              </div>

              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.risk.impact')} *</label>
                <select
                  style={formStyles.select}
                  value={formData.impact}
                  onChange={e => setFormData({ ...formData, impact: e.target.value as RiskFormData['impact'] })}
                >
                  <option value="low">{t('projects.risks.impact.low')}</option>
                  <option value="medium">{t('projects.risks.impact.medium')}</option>
                  <option value="high">{t('projects.risks.impact.high')}</option>
                </select>
              </div>
            </div>

            <div style={formStyles.row}>
              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('common.status')} *</label>
                <select
                  style={formStyles.select}
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value as RiskFormData['status'] })}
                >
                  <option value="identified">{t('projects.risks.status.identified')}</option>
                  <option value="mitigating">{t('projects.risks.status.mitigating')}</option>
                  <option value="resolved">{t('projects.risks.status.resolved')}</option>
                  <option value="accepted">{t('projects.risks.status.accepted')}</option>
                </select>
              </div>

              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.risk.owner')} *</label>
                <input
                  type="text"
                  style={formStyles.input}
                  value={formData.owner}
                  onChange={e => setFormData({ ...formData, owner: e.target.value })}
                  placeholder={t('projects.widgets.risk.ownerPlaceholder')}
                  required
                />
              </div>
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.risk.mitigationPlan')}</label>
              <textarea
                style={formStyles.textarea}
                value={formData.mitigation || ''}
                onChange={e => setFormData({ ...formData, mitigation: e.target.value })}
                placeholder={t('projects.widgets.risk.mitigationPlaceholder')}
              />
            </div>

            {/* Risk Matrix Preview */}
            <div style={{
              padding: '1rem',
              background: 'var(--bg-light)',
              borderRadius: '0.5rem',
              marginTop: '1rem',
            }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                {t('projects.widgets.risk.riskMatrix')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background:
                    (formData.probability === 'high' && formData.impact === 'high') ? '#fee2e2' :
                    (formData.probability === 'high' || formData.impact === 'high') ? '#fef3c7' :
                    '#dcfce7',
                  color:
                    (formData.probability === 'high' && formData.impact === 'high') ? '#dc2626' :
                    (formData.probability === 'high' || formData.impact === 'high') ? '#d97706' :
                    '#22c55e',
                }}>
                  {getRiskLevelLabel()}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  {t('projects.widgets.risk.riskLevelDescription')}
                </div>
              </div>
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(riskId)}
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// 5. TICKET WIDGET - Ticket management
// =============================================================================

export interface TicketFormData {
  title: string;
  description: string;
  type: 'bug' | 'feature' | 'support' | 'question';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'waiting' | 'resolved' | 'closed';
  assignee?: string;
}

interface TicketWidgetProps {
  isOpen: boolean;
  ticketId?: string;
  initialData?: Partial<TicketFormData>;
  onClose: () => void;
  onSave: (data: TicketFormData) => void;
  onDelete?: (id: string) => void;
}

// Mock tickets for loading by ID (temporary until store is implemented)
const TICKET_DATA: Record<string, TicketFormData> = {
  'tkt-001': { title: 'Login not working on Safari', description: 'Safari users are unable to log in', type: 'bug', priority: 'high', status: 'in-progress', assignee: 'Giuseppe Verdi' },
  'tkt-002': { title: 'Add PDF export', description: 'Request to export reports in PDF format', type: 'feature', priority: 'medium', status: 'open', assignee: '' },
  'tkt-003': { title: 'How to reset password?', description: 'A user asks about password reset', type: 'support', priority: 'low', status: 'resolved', assignee: 'Laura Bianchi' },
};

export function TicketWidget({ isOpen, ticketId, initialData, onClose, onSave, onDelete }: TicketWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    description: '',
    type: 'support',
    priority: 'medium',
    status: 'open',
    assignee: '',
  });

  useEffect(() => {
    // Load from ticketId first, then initialData
    if (ticketId && TICKET_DATA[ticketId]) {
      setFormData(TICKET_DATA[ticketId]);
    } else if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [ticketId, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const isEditing = !!ticketId;

  const typeLabels: Record<TicketFormData['type'], string> = {
    bug: t('projects.widgets.ticket.types.bug'),
    feature: t('projects.widgets.ticket.types.feature'),
    support: t('projects.widgets.ticket.types.support'),
    question: t('projects.widgets.ticket.types.question')
  };
  const typeColors = { bug: '#dc2626', feature: '#8b5cf6', support: '#3b82f6', question: '#22c55e' };

  const priorityLabels: Record<TicketFormData['priority'], string> = {
    low: t('projects.widgets.ticket.priorities.low'),
    medium: t('projects.widgets.ticket.priorities.medium'),
    high: t('projects.widgets.ticket.priorities.high'),
    urgent: t('projects.widgets.ticket.priorities.urgent')
  };

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <Flag size={20} />
            {isEditing ? t('projects.widgets.ticket.editTitle') : t('projects.widgets.ticket.newTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.ticket.title')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('projects.widgets.ticket.titlePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.row}>
              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.ticket.type')} *</label>
                <select
                  style={formStyles.select}
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value as TicketFormData['type'] })}
                >
                  <option value="bug">{t('projects.widgets.ticket.types.bug')}</option>
                  <option value="feature">{t('projects.widgets.ticket.types.feature')}</option>
                  <option value="support">{t('projects.widgets.ticket.types.support')}</option>
                  <option value="question">{t('projects.widgets.ticket.types.question')}</option>
                </select>
              </div>

              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.ticket.priority')} *</label>
                <select
                  style={formStyles.select}
                  value={formData.priority}
                  onChange={e => setFormData({ ...formData, priority: e.target.value as TicketFormData['priority'] })}
                >
                  <option value="low">{t('projects.widgets.ticket.priorities.low')}</option>
                  <option value="medium">{t('projects.widgets.ticket.priorities.medium')}</option>
                  <option value="high">{t('projects.widgets.ticket.priorities.high')}</option>
                  <option value="urgent">{t('projects.widgets.ticket.priorities.urgent')}</option>
                </select>
              </div>
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.description')} *</label>
              <textarea
                style={{ ...formStyles.textarea, minHeight: '150px' }}
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('projects.widgets.ticket.descriptionPlaceholder')}
                required
              />
            </div>

            <div style={formStyles.row}>
              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('common.status')}</label>
                <select
                  style={formStyles.select}
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value as TicketFormData['status'] })}
                >
                  <option value="open">{t('projects.widgets.ticket.statuses.open')}</option>
                  <option value="in-progress">{t('projects.widgets.ticket.statuses.inProgress')}</option>
                  <option value="waiting">{t('projects.widgets.ticket.statuses.waiting')}</option>
                  <option value="resolved">{t('projects.widgets.ticket.statuses.resolved')}</option>
                  <option value="closed">{t('projects.widgets.ticket.statuses.closed')}</option>
                </select>
              </div>

              <div style={{ ...formStyles.formGroup, ...formStyles.col }}>
                <label style={formStyles.label}>{t('projects.widgets.ticket.assignee')}</label>
                <input
                  type="text"
                  style={formStyles.input}
                  value={formData.assignee || ''}
                  onChange={e => setFormData({ ...formData, assignee: e.target.value })}
                  placeholder={t('projects.widgets.ticket.assigneePlaceholder')}
                />
              </div>
            </div>

            {/* Ticket Preview */}
            <div style={{
              padding: '1rem',
              background: 'var(--bg-light)',
              borderRadius: '0.5rem',
              marginTop: '1rem',
              borderLeft: `4px solid ${typeColors[formData.type]}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  background: `${typeColors[formData.type]}20`,
                  color: typeColors[formData.type],
                }}>
                  {typeLabels[formData.type]}
                </span>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  background: formData.priority === 'urgent' ? '#fee2e2' : formData.priority === 'high' ? '#fef3c7' : '#f3f4f6',
                  color: formData.priority === 'urgent' ? '#dc2626' : formData.priority === 'high' ? '#d97706' : '#6b7280',
                }}>
                  {priorityLabels[formData.priority]}
                </span>
              </div>
              <div style={{ fontWeight: 500 }}>{formData.title || t('projects.widgets.ticket.titlePlaceholderPreview')}</div>
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(ticketId)}
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// 6. MESSAGE DETAIL - Message viewing and replies
// =============================================================================

export interface MessageData {
  id: string;
  author: { name: string; avatar?: string };
  content: string;
  createdAt: string;
  replies?: MessageData[];
  attachments?: { name: string; size: string }[];
  isPinned?: boolean;
}

interface MessageDetailProps {
  isOpen: boolean;
  message: MessageData | null;
  onClose: () => void;
  onReply: (content: string) => void;
  onPin?: () => void;
  onDelete?: () => void;
}

export function MessageDetail({ isOpen, message, onClose, onReply, onPin, onDelete }: MessageDetailProps) {
  const { t } = useTranslation();
  const [replyContent, setReplyContent] = useState('');

  if (!isOpen || !message) return null;

  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (replyContent.trim()) {
      onReply(replyContent);
      setReplyContent('');
    }
  };

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <MessageSquare size={20} />
            {t('projects.widgets.message.discussion')}
          </div>
          <div style={formStyles.headerActions}>
            {onPin && (
              <button
                style={formStyles.buttonIcon}
                onClick={onPin}
                title={message.isPinned ? t('projects.widgets.message.unpin') : t('projects.widgets.message.pin')}
              >
                <Star size={18} fill={message.isPinned ? 'currentColor' : 'none'} />
              </button>
            )}
            {onDelete && (
              <button style={formStyles.buttonIcon} onClick={onDelete} title={t('common.delete')}>
                <Trash2 size={18} />
              </button>
            )}
            <button style={formStyles.buttonIcon} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={formStyles.content}>
          {/* Original Message */}
          <div style={{
            padding: '1rem',
            background: message.isPinned ? 'var(--brand-light, #eff6ff)' : 'var(--bg-light)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: message.isPinned ? '1px solid var(--brand)' : '1px solid var(--line)',
          }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 600,
                flexShrink: 0,
              }}>
                {message.author.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 600 }}>{message.author.name}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                    {new Date(message.createdAt).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>{message.content}</div>

                {message.attachments && message.attachments.length > 0 && (
                  <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {message.attachments.map((file, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: 'var(--bg-base)',
                        borderRadius: '0.375rem',
                        fontSize: '0.8125rem',
                        cursor: 'pointer',
                      }}>
                        <Paperclip size={14} />
                        <span>{file.name}</span>
                        <span style={{ color: 'var(--text-muted)' }}>({file.size})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Replies */}
          {message.replies && message.replies.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'var(--text-muted)',
                marginBottom: '0.75rem',
              }}>
                {t('projects.widgets.message.repliesCount', { count: message.replies.length })}
              </div>
              {message.replies.map(reply => (
                <div key={reply.id} style={{
                  padding: '0.875rem',
                  background: 'var(--bg-light)',
                  borderRadius: '0.5rem',
                  marginBottom: '0.5rem',
                  marginLeft: '2rem',
                  borderLeft: '2px solid var(--brand)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{reply.author.name}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {new Date(reply.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9375rem' }}>{reply.content}</div>
                </div>
              ))}
            </div>
          )}

          {/* Reply Input */}
          <form onSubmit={handleSubmitReply}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.message.writeReply')}</label>
              <textarea
                style={formStyles.textarea}
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                placeholder={t('projects.widgets.message.replyPlaceholder')}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="submit"
                style={{ ...formStyles.button, ...formStyles.buttonPrimary }}
                disabled={!replyContent.trim()}
              >
                <Send size={16} /> {t('projects.widgets.message.reply')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 7. PROOF WIDGET - Proof management
// =============================================================================

export interface ProofFormData {
  title: string;
  description?: string;
  fileUrl?: string;
  status: 'pending' | 'approved' | 'rejected' | 'changes-requested';
  reviewers: string[];
}

interface ProofWidgetProps {
  isOpen: boolean;
  proofId?: string;
  initialData?: Partial<ProofFormData>;
  onClose: () => void;
  onSave: (data: ProofFormData) => void;
  onDelete?: (id: string) => void;
}

export function ProofWidget({ isOpen, proofId, initialData, onClose, onSave, onDelete }: ProofWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ProofFormData>({
    title: '',
    description: '',
    fileUrl: '',
    status: 'pending',
    reviewers: [],
  });
  const [newReviewer, setNewReviewer] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const addReviewer = () => {
    if (newReviewer.trim() && !formData.reviewers.includes(newReviewer.trim())) {
      setFormData({ ...formData, reviewers: [...formData.reviewers, newReviewer.trim()] });
      setNewReviewer('');
    }
  };

  const removeReviewer = (reviewer: string) => {
    setFormData({ ...formData, reviewers: formData.reviewers.filter(r => r !== reviewer) });
  };

  const isEditing = !!proofId;

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={formStyles.panel} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <Eye size={20} />
            {isEditing ? t('projects.widgets.proof.editTitle') : t('projects.widgets.proof.uploadTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.proof.title')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('projects.widgets.proof.titlePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.description')}</label>
              <textarea
                style={formStyles.textarea}
                value={formData.description || ''}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('projects.widgets.proof.descriptionPlaceholder')}
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.proof.file')}</label>
              <div style={{
                border: '2px dashed var(--line)',
                borderRadius: '0.5rem',
                padding: '2rem',
                textAlign: 'center',
                cursor: 'pointer',
                background: 'var(--bg-light)',
              }}>
                <Upload size={32} style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.9375rem', marginBottom: '0.25rem' }}>
                  {t('projects.widgets.proof.dropzone')}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  {t('projects.widgets.proof.fileTypes')}
                </div>
              </div>
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.status')}</label>
              <select
                style={formStyles.select}
                value={formData.status}
                onChange={e => setFormData({ ...formData, status: e.target.value as ProofFormData['status'] })}
              >
                <option value="pending">{t('projects.widgets.proof.statuses.pending')}</option>
                <option value="approved">{t('projects.widgets.proof.statuses.approved')}</option>
                <option value="rejected">{t('projects.widgets.proof.statuses.rejected')}</option>
                <option value="changes-requested">{t('projects.widgets.proof.statuses.changesRequested')}</option>
              </select>
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.proof.reviewers')}</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  style={{ ...formStyles.input, flex: 1 }}
                  value={newReviewer}
                  onChange={e => setNewReviewer(e.target.value)}
                  placeholder={t('projects.widgets.proof.reviewerPlaceholder')}
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addReviewer())}
                />
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonSecondary }}
                  onClick={addReviewer}
                >
                  {t('common.add')}
                </button>
              </div>
              {formData.reviewers.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {formData.reviewers.map(reviewer => (
                    <span
                      key={reviewer}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.375rem 0.625rem',
                        background: 'var(--bg-light)',
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                      }}
                    >
                      <User size={14} />
                      {reviewer}
                      <button
                        type="button"
                        onClick={() => removeReviewer(reviewer)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(proofId)}
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('projects.widgets.proof.upload')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// 8. FORM BUILDER WIDGET - Custom form management
// =============================================================================

export interface FormFieldData {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'email' | 'number';
  label: string;
  required: boolean;
  options?: string[]; // for select
}

export interface CustomFormData {
  title: string;
  description?: string;
  fields: FormFieldData[];
}

interface FormBuilderWidgetProps {
  isOpen: boolean;
  formId?: string;
  initialData?: Partial<CustomFormData>;
  onClose: () => void;
  onSave: (data: CustomFormData) => void;
  onDelete?: (id: string) => void;
}

export function FormBuilderWidget({ isOpen, formId, initialData, onClose, onSave, onDelete }: FormBuilderWidgetProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CustomFormData>({
    title: '',
    description: '',
    fields: [],
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const addField = (type: FormFieldData['type']) => {
    const newField: FormFieldData = {
      id: `field-${Date.now()}`,
      type,
      label: '',
      required: false,
    };
    setFormData({ ...formData, fields: [...formData.fields, newField] });
  };

  const updateField = (id: string, updates: Partial<FormFieldData>) => {
    setFormData({
      ...formData,
      fields: formData.fields.map(f => f.id === id ? { ...f, ...updates } : f),
    });
  };

  const removeField = (id: string) => {
    setFormData({ ...formData, fields: formData.fields.filter(f => f.id !== id) });
  };

  const isEditing = !!formId;

  const fieldTypeLabels: Record<FormFieldData['type'], string> = {
    text: t('projects.widgets.formBuilder.fieldTypes.text'),
    textarea: t('projects.widgets.formBuilder.fieldTypes.textarea'),
    select: t('projects.widgets.formBuilder.fieldTypes.select'),
    checkbox: t('projects.widgets.formBuilder.fieldTypes.checkbox'),
    date: t('projects.widgets.formBuilder.fieldTypes.date'),
    email: t('projects.widgets.formBuilder.fieldTypes.email'),
    number: t('projects.widgets.formBuilder.fieldTypes.number'),
  };

  return (
    <div style={formStyles.overlay} onClick={onClose}>
      <div style={{ ...formStyles.panel, maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
        <div style={formStyles.header}>
          <div style={formStyles.headerTitle}>
            <FileText size={20} />
            {isEditing ? t('projects.widgets.formBuilder.editTitle') : t('projects.widgets.formBuilder.newTitle')}
          </div>
          <button style={formStyles.buttonIcon} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={formStyles.content}>
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.formBuilder.formName')} *</label>
              <input
                type="text"
                style={formStyles.input}
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('projects.widgets.formBuilder.formNamePlaceholder')}
                required
              />
            </div>

            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('common.description')}</label>
              <textarea
                style={formStyles.textarea}
                value={formData.description || ''}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('projects.widgets.formBuilder.descriptionPlaceholder')}
              />
            </div>

            {/* Fields */}
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t('projects.widgets.formBuilder.formFields')}</label>

              {formData.fields.map((field, index) => (
                <div key={field.id} style={{
                  padding: '1rem',
                  background: 'var(--bg-light)',
                  borderRadius: '0.5rem',
                  marginBottom: '0.75rem',
                  border: '1px solid var(--line)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)' }}>
                      {t('projects.widgets.formBuilder.fieldNumber', { number: index + 1 })} - {fieldTypeLabels[field.type]}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeField(field.id)}
                      style={formStyles.buttonIcon}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        style={formStyles.input}
                        value={field.label}
                        onChange={e => updateField(field.id, { label: e.target.value })}
                        placeholder={t('projects.widgets.formBuilder.fieldLabel')}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={e => updateField(field.id, { required: e.target.checked })}
                      />
                      <span style={{ fontSize: '0.8125rem' }}>{t('projects.widgets.formBuilder.required')}</span>
                    </label>
                  </div>
                </div>
              ))}

              {/* Add Field Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                {(Object.keys(fieldTypeLabels) as FormFieldData['type'][]).map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addField(type)}
                    style={{ ...formStyles.button, ...formStyles.buttonSecondary, fontSize: '0.75rem', padding: '0.375rem 0.625rem' }}
                  >
                    <Plus size={14} /> {fieldTypeLabels[type]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={formStyles.footer}>
            <div>
              {isEditing && onDelete && (
                <button
                  type="button"
                  style={{ ...formStyles.button, ...formStyles.buttonDanger }}
                  onClick={() => onDelete(formId)}
                >
                  <Trash2 size={16} /> {t('common.delete')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={{ ...formStyles.button, ...formStyles.buttonSecondary }} onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" style={{ ...formStyles.button, ...formStyles.buttonPrimary }}>
                <Save size={16} /> {isEditing ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
